import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { TopBar } from "@/components/TopBar";
import { useDiagnostic } from "@/context/DiagnosticContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight } from "lucide-react";

export default function Diagnostic() {
  const navigate = useNavigate();
  const { session, setAnswer, setCurrent } = useDiagnostic();
  const { questions, answers, respondentId } = session;
  const [submitting, setSubmitting] = useState(false);

  if (!respondentId || !questions?.length) {
    navigate("/pathway");
    return null;
  }

  const total = questions.length;
  // Persisted position, clamped to the valid range in case questions changed.
  const current = Math.min(Math.max(session.current || 0, 0), total - 1);
  const q = questions[current];
  const selected = answers[q.index];
  const progress = Math.round(((current + (selected != null ? 1 : 0)) / total) * 100);

  const handleBack = () => {
    if (current === 0) navigate("/about");
    else setCurrent(current - 1);
  };

  const finish = async () => {
    setSubmitting(true);
    try {
      const payload = questions.map((qq) => ({
        question_id: qq.id,
        option_index: answers[qq.index],
      }));
      await api.submitAnswers(respondentId, payload);
      navigate("/analysing");
    } catch {
      toast.error("Could not save your answers. Please try again.");
      setSubmitting(false);
    }
  };

  const handleNext = () => {
    if (selected == null) {
      toast.error("Select an option to continue.");
      return;
    }
    if (current === total - 1) finish();
    else setCurrent(current + 1);
  };

  return (
    <div className="flex min-h-[100dvh] flex-col bg-white">
      <TopBar step={`Question ${current + 1} of ${total}`} />

      {/* progress bar */}
      <div className="h-0.5 w-full bg-neutral-100">
        <div
          className="h-full bg-[#1408F7] transition-all duration-300"
          style={{ width: `${progress}%` }}
          data-testid="diagnostic-progress-bar"
        />
      </div>

      <main className="mx-auto flex w-full max-w-3xl flex-1 flex-col px-4 py-6 sm:px-6 sm:py-8 lg:py-10">
        <div key={q.index} className="tw-fade-up">
          <h1
            className="text-xl font-semibold leading-snug tracking-tight text-neutral-900 sm:text-2xl lg:text-3xl"
            data-testid="diagnostic-question-text"
          >
            {q.text}
          </h1>

          <div className="mt-6 space-y-2.5 sm:mt-8 sm:space-y-3">
            {q.options.map((opt, i) => {
              const active = selected === i;
              return (
                <button
                  key={opt.key}
                  onClick={() => setAnswer(q.index, i)}
                  className={`flex w-full items-center gap-3 rounded-sm border p-4 text-left transition-colors duration-200 sm:gap-4 sm:p-5 ${
                    active
                      ? "border-2 border-[#1408F7] bg-[var(--tw-blue-tint)]"
                      : "border border-neutral-200 hover:border-neutral-400"
                  }`}
                  data-testid={`diagnostic-option-${i}`}
                >
                  <span
                    className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-sm border text-sm font-semibold ${
                      active
                        ? "border-[#1408F7] bg-[#1408F7] text-white"
                        : "border-neutral-300 text-neutral-500"
                    }`}
                  >
                    {opt.key}
                  </span>
                  <span className="text-sm text-neutral-800 sm:text-base">{opt.text}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="mt-8 flex items-center justify-between border-t border-neutral-200 pt-5 sm:pt-6">
          <button
            onClick={handleBack}
            className="tw-focus inline-flex items-center gap-2 rounded-sm px-3 py-3 text-sm font-semibold uppercase tracking-wide text-neutral-500 hover:text-neutral-900 sm:px-4"
            data-testid="diagnostic-back-button"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </button>
          <button
            onClick={handleNext}
            disabled={submitting || selected == null}
            className="tw-btn-primary tw-focus inline-flex items-center gap-2 rounded-sm px-6 py-4 text-sm font-semibold uppercase tracking-wide disabled:cursor-not-allowed disabled:opacity-30 sm:px-8"
            data-testid="diagnostic-next-button"
          >
            {current === total - 1 ? (submitting ? "Analysing…" : "See results") : "Continue"}
            <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </main>
    </div>
  );
}
